'use client';

import {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import { findFullWidthStopper } from '../utils/findFullWidthStopper';
import MobileChallengeCapsule from './MobileChallengeCapsule';

// `useSyncExternalStore` needs a stable subscribe reference — a no-op store is
// enough because the value we're observing ("is this the client?") never
// changes after hydration completes.
const NEVER_CHANGES = () => () => {};
const RETURN_TRUE = () => true;
const RETURN_FALSE = () => false;

/**
 * Returns `false` during SSR and the initial client render (matching the
 * server-rendered HTML so hydration doesn't warn), `true` on every subsequent
 * render. Replaces the older `useEffect(() => setState(true))` idiom in a way
 * that satisfies `react-hooks/set-state-in-effect`.
 */
function useHasMounted(): boolean {
  return useSyncExternalStore(NEVER_CHANGES, RETURN_TRUE, RETURN_FALSE);
}

// Matches the sticky offset already used by BlogToc (article-page TOC) so the
// aside settles at the same visual anchor across page types.
const TOP_OFFSET_DESKTOP_PX = 120;
// Mobile mirrors the same offset — the sticky mobile header + promo banner
// stays under this line, so the capsule never tucks under the chrome.
const TOP_OFFSET_MOBILE_PX = 125;
// The stopper's own top margin can visually overlap the pinned aside; nudge
// the pin point up so the two never touch.
const PIN_BREATHING_ROOM_PX = 24;
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

interface Props {
  children: ReactNode;
  /**
   * Eyebrow text for the mobile disclosure capsule (renders above the title).
   * Defaults to the sidebar's own "CLIENT" convention.
   */
  mobileCapsuleEyebrow?: string;
  /**
   * Main label for the mobile disclosure capsule. Callers pass the case
   * study's client name so the collapsed pill reads e.g. "FOX Sports (FOX
   * Tech)" instead of a static string.
   */
  mobileCapsuleTitle?: string;
}

type DesktopPosition =
  | { mode: 'flow' }
  | {
      mode: 'fixed';
      top: number;
      left: number;
      width: number;
    }
  | {
      mode: 'pinned';
      top: number;
      left: number;
      width: number;
    };

/**
 * Turns the CaseStudyChallenge sidebar into a scroll-aware rail that outlives
 * its own slice section.
 *
 * Desktop (>= md, 768px+):
 *   • While the aside's natural position is on screen, it stays in flow.
 *   • Once the user scrolls past its top, a portaled copy takes over with
 *     `position: fixed` at `TOP_OFFSET_DESKTOP_PX` below the viewport top.
 *   • The pinned aside stops when the next full-width slice scrolls up to
 *     meet it — see `findFullWidthStopper` for the detection heuristic.
 *
 * Mobile (< md):
 *   • The aside renders in place at the top of the CaseStudyChallenge stack.
 *   • Once scrolled past, `MobileChallengeCapsule` takes over as a compact
 *     disclosure pinned to the top of the viewport.
 *   • No full-width stopper on mobile: every slice is full-width by design,
 *     so the capsule stays available until the page runs out.
 *
 * Both flavours use a portal because slices are DOM siblings inside
 * `<SliceZone>` with no shared wrapper we can hang a longer `position: sticky`
 * boundary off of. The portal keeps the visible aside detached from its
 * slice's containing block, while the in-place placeholder reserves grid
 * space so the challenge section's own two-column layout doesn't collapse.
 */
export default function StickyChallengeSidebar({
  children,
  mobileCapsuleEyebrow = 'Client',
  mobileCapsuleTitle,
}: Props) {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const desktopCloneRef = useRef<HTMLDivElement>(null);
  const mounted = useHasMounted();
  const [desktopPosition, setDesktopPosition] = useState<DesktopPosition>({
    mode: 'flow',
  });
  const [mobileCapsuleVisible, setMobileCapsuleVisible] = useState(false);

  const updatePosition = useCallback(() => {
    const placeholder = placeholderRef.current;
    const clone = desktopCloneRef.current;
    if (!placeholder || !clone) {
      return;
    }

    const challengeSection = placeholder.closest<HTMLElement>(
      'section[data-slice-type="case_study_challenge"]',
    );
    if (!challengeSection) {
      return;
    }

    const desktop = window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
    const topOffset = desktop ? TOP_OFFSET_DESKTOP_PX : TOP_OFFSET_MOBILE_PX;

    const pRect = placeholder.getBoundingClientRect();
    const naturalTopDoc = pRect.top + window.scrollY;
    const naturalLeftDoc = pRect.left + window.scrollX;
    const width = placeholder.offsetWidth;

    const scroll = window.scrollY;
    const stickyStart = naturalTopDoc - topOffset;

    if (desktop) {
      // Mobile capsule stays hidden while desktop is active — a viewport
      // resize could otherwise leave it stuck on screen.
      setMobileCapsuleVisible(false);

      const asideHeight = clone.offsetHeight || placeholder.offsetHeight;
      const stopper = findFullWidthStopper(challengeSection, pRect);
      const stopY = stopper
        ? stopper.getBoundingClientRect().top +
          window.scrollY -
          topOffset -
          asideHeight -
          PIN_BREATHING_ROOM_PX
        : null;

      if (scroll < stickyStart) {
        setDesktopPosition((prev) =>
          prev.mode === 'flow' ? prev : { mode: 'flow' },
        );
        return;
      }

      if (stopY === null || scroll < stopY) {
        setDesktopPosition({
          mode: 'fixed',
          top: topOffset,
          left: pRect.left,
          width,
        });
        return;
      }

      setDesktopPosition({
        mode: 'pinned',
        top: stopY + topOffset,
        left: naturalLeftDoc,
        width,
      });
      return;
    }

    // Mobile: the desktop rail stays inert and the aside just stays in flow.
    setDesktopPosition((prev) =>
      prev.mode === 'flow' ? prev : { mode: 'flow' },
    );
    // Capsule appears once the aside's natural top has scrolled above the
    // mobile offset — same trigger point the desktop rail uses.
    const nextVisible = scroll >= stickyStart;
    setMobileCapsuleVisible((prev) =>
      prev === nextVisible ? prev : nextVisible,
    );
  }, []);

  useLayoutEffect(() => {
    if (!mounted) {
      return;
    }

    let rafId = 0;
    const schedule = () => {
      if (rafId) {
        return;
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updatePosition();
      });
    };

    // Initial sync is deferred to the same rAF pipeline as scroll/resize
    // updates. Calling `updatePosition()` directly here would fire setState
    // synchronously from within the effect body (see
    // `react-hooks/set-state-in-effect`); routing through `schedule()` moves
    // the setState into a rAF callback instead. The one-frame delay is
    // imperceptible — before this frame paints, the aside is still in its
    // natural in-flow position, which is the correct initial state anyway.
    schedule();

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    // Slice contents below the aside can change height as images decode or
    // Prismic content hydrates; a body-level observer catches any of it.
    const ro = new ResizeObserver(schedule);
    ro.observe(document.body);

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
    };
  }, [mounted, updatePosition]);

  const desktopCloneStyle: React.CSSProperties =
    desktopPosition.mode === 'flow'
      ? {
          visibility: 'hidden',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 0,
        }
      : {
          position: desktopPosition.mode === 'fixed' ? 'fixed' : 'absolute',
          top: desktopPosition.top,
          left: desktopPosition.left,
          width: desktopPosition.width,
          zIndex: 20,
        };

  return (
    <>
      {/* Placeholder keeps the CaseStudyChallenge grid column reserved. On
          desktop the portal takes over as soon as we scroll past its top, so
          it hides visually but stays in layout so the main column width
          doesn't shift. On mobile it renders in flow the whole time — the
          capsule (see below) only shows AFTER the user has scrolled past it. */}
      <div
        ref={placeholderRef}
        aria-hidden={mounted && desktopPosition.mode !== 'flow'}
        style={
          mounted && desktopPosition.mode !== 'flow'
            ? { visibility: 'hidden' }
            : undefined
        }
      >
        {children}
      </div>

      {mounted &&
        createPortal(
          <>
            <div
              ref={desktopCloneRef}
              className="hidden md:block"
              style={desktopCloneStyle}
              aria-hidden={desktopPosition.mode === 'flow'}
            >
              {children}
            </div>

            {mobileCapsuleVisible && (
              <MobileChallengeCapsule
                eyebrow={mobileCapsuleEyebrow}
                title={mobileCapsuleTitle}
                topOffsetPx={TOP_OFFSET_MOBILE_PX}
              >
                {children}
              </MobileChallengeCapsule>
            )}
          </>,
          document.body,
        )}
    </>
  );
}
